import { SessionDetail } from "@/utils/types"

import { dexie } from "../../db"
import { logDexieError } from "../../utils/logDexieError"

// ─── Types ─────────────────────────────────────────────────────────────────────

export type SessionLocal = SessionDetail & {
  dexieId: number
  // Set the moment the user confirms the consent screen offline; this is the timestamp we upload
  consentScreenSeenAt?: Date
  // Set once the local seen-at has been synced to the backend; absence = pending upload
  consentScreenSyncedAt?: Date
}

// ─── Methods ───────────────────────────────────────────────────────────────────

export async function upsertSession(data: Omit<SessionLocal, "dexieId">): Promise<void> {
  try {
    await dexie.transaction("rw", dexie.session, async () => {
      const existing = await dexie.session.where("id").equals(data.id).first()
      if (existing) {
        await dexie.session.update(existing.dexieId, data)
      } else {
        await dexie.session.add(data as SessionLocal)
      }
    })
  } catch (error) {
    logDexieError(error)
  }
}

export async function getSession(id: string): Promise<SessionLocal | undefined> {
  try {
    return await dexie.session.where("id").equals(id).first()
  } catch (error) {
    logDexieError(error)
  }
}

// Records locally that the user confirmed the consent screen; the BackgroundUploader syncs it to the backend
export async function markConsentScreenSeen(sessionId: string): Promise<void> {
  try {
    await dexie.session.where("id").equals(sessionId).modify({ consentScreenSeenAt: new Date() })
  } catch (error) {
    logDexieError(error)
  }
}

// Marks the consent seen-at as successfully uploaded so it stops being retried
export async function markConsentScreenSynced(sessionId: string): Promise<void> {
  try {
    await dexie.session.where("id").equals(sessionId).modify({ consentScreenSyncedAt: new Date() })
  } catch (error) {
    logDexieError(error)
  }
}
